/**
 * Returns a logo URL for a financial institution name.
 * Uses Clearbit Logo API (free, no auth) with a curated domain map for common
 * Canadian institutions. Falls back to a domain guess if not in the map.
 * Always supply an onError handler in the <img> to show a fallback avatar.
 */

const DOMAINS: Record<string, string> = {
  // Big 6
  'td': 'td.com',
  'td bank': 'td.com',
  'toronto-dominion': 'td.com',
  'toronto dominion': 'td.com',
  'rbc': 'rbc.com',
  'royal bank': 'rbc.com',
  'royal bank of canada': 'rbc.com',
  'scotiabank': 'scotiabank.com',
  'the bank of nova scotia': 'scotiabank.com',
  'bank of nova scotia': 'scotiabank.com',
  'scotia': 'scotiabank.com',
  'bmo': 'bmo.com',
  'bank of montreal': 'bmo.com',
  'cibc': 'cibc.com',
  'national bank': 'nbc.ca',
  'national bank of canada': 'nbc.ca',
  'banque nationale': 'nbc.ca',
  // Digital / online banks
  'wealthsimple': 'wealthsimple.com',
  'tangerine': 'tangerine.ca',
  'eq bank': 'eqbank.ca',
  'eqbank': 'eqbank.ca',
  'simplii': 'simplii.com',
  'simplii financial': 'simplii.com',
  'pc financial': 'pcfinancial.ca',
  'presidents choice financial': 'pcfinancial.ca',
  'motusbank': 'motusbank.ca',
  'laurentian bank': 'laurentianbank.ca',
  'hsbc': 'hsbc.ca',
  'hsbc canada': 'hsbc.ca',
  // Brokerages / investment
  'questrade': 'questrade.com',
  'qtrade': 'qtrade.ca',
  'disnat': 'disnat.com',
  'interactive brokers': 'interactivebrokers.com',
  'ibkr': 'interactivebrokers.com',
  'fidelity': 'fidelity.ca',
  'vanguard': 'vanguard.com',
  'blackrock': 'blackrock.com',
  'ishares': 'ishares.com',
  'ci direct': 'cidirectinvesting.com',
  'wealthbar': 'wealthbar.com',
  'nest wealth': 'nestwealth.com',
  'justwealth': 'justwealth.com',
  // Insurance / wealth
  'manulife': 'manulife.ca',
  'sun life': 'sunlife.ca',
  'sunlife': 'sunlife.ca',
  'great-west life': 'greatwestlife.com',
  'great west life': 'greatwestlife.com',
  'canada life': 'canadalife.com',
  'industrial alliance': 'ia.ca',
  'ia financial': 'ia.ca',
  'empire life': 'empirelife.ca',
  'equitable life': 'equitable.ca',
  'ivari': 'ivari.ca',
  // Credit unions
  'desjardins': 'desjardins.com',
  'meridian': 'meridiancu.ca',
  'first ontario': 'firstontario.com',
  'servus': 'servus.ca',
  'coast capital': 'coastcapitalsavings.com',
  'vancity': 'vancity.com',
  'conexus': 'conexus.ca',
  'affinity': 'affinitycu.ca',
  // Crypto / fintech
  'coinbase': 'coinbase.com',
  'kraken': 'kraken.com',
  'newton': 'newton.co',
  'shakepay': 'shakepay.com',
  'ndax': 'ndax.io',
  'bitbuy': 'bitbuy.ca',
  // Real estate platforms
  'addy': 'addyinvest.com',
  'equiton': 'equiton.com',
};

function resolveDomain(institution: string): string | null {
  const key = institution.trim().toLowerCase();
  if (DOMAINS[key]) return DOMAINS[key];

  // Partial match
  for (const [k, domain] of Object.entries(DOMAINS)) {
    if (key.includes(k) || k.includes(key)) return domain;
  }

  // Best-effort guess: strip spaces, append .com
  return key.replace(/\s+/g, '') + '.com';
}

/**
 * Returns an ordered list of logo URLs to try for a given institution.
 * The caller should try each in sequence, advancing on error.
 */
export function getInstitutionLogoSources(institution: string, plaidLogoUrl?: string): string[] {
  const sources: string[] = [];
  if (plaidLogoUrl) sources.push(plaidLogoUrl);

  const domain = resolveDomain(institution);
  if (domain) {
    // icon.horse: free, no auth, high-quality brand icons
    sources.push(`https://icon.horse/icon/${domain}`);
    // Google favicon cache: near-100% reliable fallback
    sources.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
  }

  return sources;
}
